import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import CustomSnackbar from "../../components/CustomSnackbar";
import ConfirmDialog from "../../components/ConfirmDialog";
import AccountService from "../AccountService";

const APP_CODE_LABELS = {
    jigsaw_muse: "Jigsaw Muse",
    sliding_puzzle_quest: "Sliding Puzzle Quest",
    word_wheel_quest: "Word Wheel Quest",
};

const DEFAULT_TITLE = "Test notification";
const DEFAULT_BODY = "This is a test notification from Puzzle Interact.";

function appLabel(appCode) {
    return APP_CODE_LABELS[appCode] || appCode || "—";
}

/**
 * Admin/testing: list a user's registered push devices and send a test notification.
 */
export default function NotificationTestAdmin({ cloudUserId }) {
    const [title, setTitle] = useState(DEFAULT_TITLE);
    const [body, setBody] = useState(DEFAULT_BODY);
    const [sending, setSending] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [tokens, setTokens] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [tokensLoading, setTokensLoading] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const subjectUserId = typeof cloudUserId === "string" ? cloudUserId.trim() : "";

    const showSnack = (severity, message) => setSnackbar({ open: true, severity, message });

    const loadTokens = useCallback(async () => {
        if (!subjectUserId) {
            setTokens([]);
            setSelectedIds([]);
            setPushEnabled(null);
            return;
        }
        setTokensLoading(true);
        try {
            const result = await AccountService.listPushDeviceTokens(subjectUserId);
            const list = Array.isArray(result?.content) ? result.content : [];
            setTokens(list);
            setSelectedIds([]);
            setPushEnabled(typeof result?.pushEnabled === "boolean" ? result.pushEnabled : null);
        } catch (e) {
            Logger.error("NotificationTestAdmin load tokens:", e);
            setTokens([]);
            setSelectedIds([]);
        } finally {
            setTokensLoading(false);
        }
    }, [subjectUserId]);

    useEffect(() => {
        loadTokens();
    }, [loadTokens]);

    const toggleDevice = (id) => {
        if (!id) return;
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const toggleAllDevices = (checked) => {
        setSelectedIds(checked ? tokens.filter((t) => t?.id).map((t) => t.id) : []);
    };

    const openDeleteDialog = (token) => {
        if (!token?.id) return;
        setDeleteTarget(token);
        setDeleteOpen(true);
    };

    const handleDeleteConfirm = async (tokenId) => {
        if (!tokenId || !subjectUserId) return;
        setDeleting(true);
        try {
            await AccountService.deletePushDeviceToken(subjectUserId, tokenId);
            showSnack("success", "Device token deleted");
            setDeleteOpen(false);
            setDeleteTarget(null);
            setSelectedIds((prev) => prev.filter((id) => id !== tokenId));
            loadTokens();
        } catch (e) {
            Logger.error("NotificationTestAdmin delete:", e);
            showSnack("error", e.message || "Failed to delete device token");
        } finally {
            setDeleting(false);
        }
    };

    const handleSend = async () => {
        if (!subjectUserId) {
            showSnack("warning", "User ID is required");
            return;
        }
        if (selectedIds.length === 0) {
            showSnack("warning", "Select at least one device");
            return;
        }
        setSending(true);
        try {
            const result = await AccountService.sendTestNotification(subjectUserId, {
                deviceTokenIds: selectedIds,
                title,
                body,
            });
            setLastResult(result || null);
            const sent = Number(result?.sent) || 0;
            const deviceTokens = Number(result?.deviceTokens) || 0;
            if (result?.pushEnabled === false) {
                showSnack("warning", result?.message || "Push delivery is disabled on the server.");
            } else if (sent > 0) {
                showSnack("success", `Sent to ${sent} of ${deviceTokens} device${deviceTokens === 1 ? "" : "s"}.`);
            } else {
                showSnack("info", result?.message || "No notifications were sent (no registered devices).");
            }
            loadTokens();
        } catch (e) {
            Logger.error("NotificationTestAdmin send:", e);
            showSnack("error", e.message || "Failed to send test notification");
        } finally {
            setSending(false);
        }
    };

    if (!subjectUserId) {
        return null;
    }

    const sendableCount = tokens.filter((t) => t?.canSend).length;

    return (
        <Card sx={{ mt: 0 }}>
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <NotificationsActiveOutlinedIcon color="action" />
                    <Typography variant="h5" component="h2" sx={{ m: 0 }}>
                        Notification
                    </Typography>
                    {pushEnabled === false && (
                        <Chip label="Push disabled on server" color="warning" size="small" variant="outlined" />
                    )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Testing only — registered devices for this user (<code>push_device_token</code>). A device can
                    receive a push once its <code>sns_endpoint_arn</code> is set.
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ m: 0 }}>
                        Registered devices ({tokens.length}
                        {tokens.length > 0 ? ` · ${sendableCount} ready` : ""})
                    </Typography>
                    <Tooltip title="Reload devices">
                        <span>
                            <IconButton size="small" onClick={loadTokens} disabled={tokensLoading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {tokensLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : tokens.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        No registered devices for this user.
                    </Alert>
                ) : (
                    <TableContainer sx={{ mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            indeterminate={
                                                selectedIds.length > 0 && selectedIds.length < tokens.length
                                            }
                                            checked={tokens.length > 0 && selectedIds.length === tokens.length}
                                            onChange={(e) => toggleAllDevices(e.target.checked)}
                                            inputProps={{ "aria-label": "Select all devices" }}
                                        />
                                    </TableCell>
                                    <TableCell>App</TableCell>
                                    <TableCell>Platform</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>SNS endpoint ARN</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Updated</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tokens.map((t) => (
                                    <TableRow
                                        key={t.id || `${t.appCode}-${t.deviceTokenPreview}`}
                                        hover
                                        selected={t.id ? selectedIds.includes(t.id) : false}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={t.id ? selectedIds.includes(t.id) : false}
                                                disabled={!t.id}
                                                onChange={() => toggleDevice(t.id)}
                                                inputProps={{
                                                    "aria-label": `Select device ${t.deviceTokenPreview || t.id || ""}`,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {appLabel(t.appCode)}
                                            </Typography>
                                            {t.deviceTokenPreview && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: "monospace" }}
                                                >
                                                    {t.deviceTokenPreview}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{t.platform || "—"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t.canSend ? "Can send" : "Not ready"}
                                                color={t.canSend ? "success" : "default"}
                                                size="small"
                                                variant={t.canSend ? "filled" : "outlined"}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 260 }}>
                                            {t.snsEndpointArn ? (
                                                <Tooltip title={t.snsEndpointArn}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontFamily: "monospace",
                                                            wordBreak: "break-all",
                                                            display: "block",
                                                        }}
                                                    >
                                                        {t.snsEndpointArn}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Not set
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{UtilService.formatDate(t.createdAt) || "—"}</TableCell>
                                        <TableCell>{UtilService.formatDate(t.updatedAt) || "—"}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Delete device token">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled={!t.id || deleting}
                                                        onClick={() => openDeleteDialog(t)}
                                                        aria-label="Delete device token"
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder={DEFAULT_TITLE}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            placeholder={DEFAULT_BODY}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, flexWrap: "wrap" }}>
                    <Button
                        variant="contained"
                        startIcon={<SendOutlinedIcon />}
                        onClick={handleSend}
                        disabled={sending || selectedIds.length === 0}
                    >
                        {sending
                            ? "Sending…"
                            : `Send test notification${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
                    </Button>
                    {lastResult && (
                        <Typography variant="body2" color="text.secondary">
                            {`Devices: ${Number(lastResult.deviceTokens) || 0} · Sent: ${Number(lastResult.sent) || 0}`}
                            {lastResult.pushEnabled === false ? " · push disabled" : ""}
                        </Typography>
                    )}
                </Box>

                {lastResult?.message && (
                    <Alert
                        severity={lastResult.pushEnabled === false ? "warning" : "info"}
                        sx={{ mt: 2 }}
                    >
                        {lastResult.message}
                    </Alert>
                )}
            </CardContent>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />

            <ConfirmDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                title="Delete device token?"
                message={[
                    "This removes the row from push_device_token and deletes the SNS endpoint (best effort).",
                    "The user can register again from the app on next launch.",
                ]}
                targetName={
                    deleteTarget
                        ? `${appLabel(deleteTarget.appCode)}${deleteTarget.deviceTokenPreview ? ` · ${deleteTarget.deviceTokenPreview}` : ""}`
                        : ""
                }
                targetId={deleteTarget?.id}
                callback={handleDeleteConfirm}
            />
        </Card>
    );
}
